import React, { useState } from 'react';
import { Text, Flexbox } from '@morressier/ts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSquarePen,
  faTrash,
  faClose,
  faSquarePlus
} from '@fortawesome/pro-regular-svg-icons';
import { faPenLine } from '@fortawesome/pro-light-svg-icons';
import { BiographyContent } from '../styles';
import { updatePartialUserProfile } from 'clients/internal/profileService';
import { IProfile } from 'typings/profile';
import { KeyedMutator } from 'swr';
import BiographyFormModal from './BiographyFormModal';
import { ExitModal, LinkButton } from 'components/ProfileUIComponents';
import MissingProfileIndicator from '../MissingProfileIndicator';
import { logEvent } from 'utils/eventTracking';

type IBiographyProps = {
  isSameUser?: boolean;
  userId: string;
  mutateProfile: KeyedMutator<IProfile>;
  profile: IProfile;
};

export const Biography = (props: IBiographyProps) => {
  const { userId, isSameUser, mutateProfile, profile } = props;
  const { biography } = profile;

  const [showEditBiographyForm, setShowEditBiographyForm] = useState(false);

  const closeEditBiographyForm = () => setShowEditBiographyForm(false);

  const handleBiographyRemoveClicked = async () => {
    logEvent('PROFILE_REMOVE_BIOGRAPHY_REMOVE_BIOGRAPHY_CLICKED');
    closeExitModal();
    await updatePartialUserProfile(userId, { biography: '' });
    await mutateProfile();
  };

  const handleBiographyCancelClicked = () => {
    logEvent('PROFILE_REMOVE_BIOGRAPHY_REMOVE_BIOGRAPHY_CANCELED');
    closeExitModal();
  };
  const [showExitModal, setShowExitModal] = useState<boolean>(false);
  const closeExitModal = () => setShowExitModal(false);
  const openExitModal = () => setShowExitModal(true);

  const openEditBiographyModal = () => {
    logEvent('PROFILE_PAGE_EDIT_BIOGRAPHY_EDIT_BIOGRAPHY_CLICKED');
    setShowEditBiographyForm(true);
  };

  const openRemoveBiographyModal = () => {
    logEvent('PROFILE_REMOVE_BIOGRAPHY_CLICKED');
    openExitModal();
  };

  const handleMissingProfileBiographyAction = () => {
    logEvent('PROFILE_PAGE_ADD_BIOGRAPHY_UPDATE_BIOGRAPHY_CLICKED');
    setShowEditBiographyForm(true);
  };

  return (
    <div id="biography">
      <Flexbox gap="16px" flexDirection="column" alignItems="flex-start">
        <Flexbox gap="8px" flexDirection="row" alignItems="center">
          <Text size="h4_new" fontWeight="semiBold">
            Biography
          </Text>
          {isSameUser && Boolean(biography) && (
            <Flexbox gap="16px">
              <LinkButton onClick={openEditBiographyModal}>
                <FontAwesomeIcon icon={faSquarePen} /> Edit
              </LinkButton>
              <LinkButton onClick={openRemoveBiographyModal}>
                <FontAwesomeIcon icon={faTrash} /> Remove
              </LinkButton>
            </Flexbox>
          )}
        </Flexbox>
        <BiographyFormModal
          isOpen={showEditBiographyForm}
          onDismiss={closeEditBiographyForm}
          mutateProfile={mutateProfile}
          profile={profile}
        />
      </Flexbox>

      {isSameUser && !Boolean(biography) && (
        <MissingProfileIndicator
          handleAction={handleMissingProfileBiographyAction}
          icon={faPenLine}
          actionIcon={faSquarePlus}
          title="Add your biography"
          actiontitle="Add biography"
          description="Write about your relevant experiences, specializations, accomplishments, and skills to tell the best story of your career."
        />
      )}

      <BiographyContent size="body1_new">{biography}</BiographyContent>
      <ExitModal
        onConfirm={handleBiographyRemoveClicked}
        onCancel={handleBiographyCancelClicked}
        isOpen={showExitModal}
        onClose={closeExitModal}
        title="Remove biography?"
        description="Your biography will be permanently removed from your profile."
        icon={faClose}
        confirmActionText="Remove biography"
        cancelActionText="Cancel"
      />
    </div>
  );
};

export default Biography;
